'use client';

import { useId, useLayoutEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Alert, TextButton } from '@zillow/constellation-10';
import { ChevronDown, ChevronUp, Tags } from 'lucide-react';
import { cx } from '@/styled-system/css';
import { LucideIcon } from '@/src/components/LucideIcon';
import type { SpecialOfferBannerContent } from './specialOfferBanner.utils';
import * as styles from './specialOfferBanner.styles';

type SpecialOfferBannerProps = {
  offer: SpecialOfferBannerContent;
};

export function SpecialOfferBanner({ offer }: SpecialOfferBannerProps) {
  const t = useTranslations('hdp');
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const descriptionId = useId();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    const description = descriptionRef.current;
    if (!description || isExpanded) return;

    const updateOverflow = () => setIsOverflowing(description.scrollHeight > description.clientHeight);
    updateOverflow();
    const resizeObserver = new ResizeObserver(updateOverflow);
    resizeObserver.observe(description);
    return () => resizeObserver.disconnect();
  }, [isExpanded, offer?.primaryDescription]);

  const hasAdditionalOffers = offer.additionalDescriptions.length > 0;
  const canExpand = isOverflowing || hasAdditionalOffers;
  return (
    <Alert.Root tone="info" role="status" className={styles.alert} data-testid="special-offer-banner">
      <Alert.Body className={styles.body}>
        <div className={styles.titleRow}>
          <span className={styles.offerIconContainer} aria-hidden="true">
            <LucideIcon icon={Tags} size="sm" className={styles.offerIcon} />
          </span>
          <strong className={styles.title}>{offer.title}</strong>
        </div>
        {offer.primaryDescription && (
          <div id={descriptionId}>
            <p ref={descriptionRef} className={cx(styles.description, !isExpanded && styles.collapsedDescription)}>
              {offer.primaryDescription}
            </p>
            {isExpanded && hasAdditionalOffers && (
              <ul className={styles.additionalOffers}>
                {offer.additionalDescriptions.map((description) => (
                  <li key={description}>{description}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        {canExpand && (
          <TextButton.Root
            type="button"
            tone="neutral"
            textStyle="body-sm"
            aria-expanded={isExpanded}
            aria-controls={descriptionId}
            onClick={() => setIsExpanded((expanded) => !expanded)}
          >
            <TextButton.Label>{isExpanded ? t('showLess') : t('showMore')}</TextButton.Label>
            <TextButton.Icon size="sm">
              <LucideIcon icon={isExpanded ? ChevronUp : ChevronDown} size="sm" />
            </TextButton.Icon>
          </TextButton.Root>
        )}
      </Alert.Body>
    </Alert.Root>
  );
}
